/**
 * Labstep
 *
 * @module components/FilterPost/NewIndex/Metadata/Form
 * @desc Filter Metadata form
 */

import { FormReusableGroup } from 'labstep-web/core/Form/Reusable/Group';
import {
  MetadataType,
  MetadataTypeValues,
} from 'labstep-web/models/metadata/types';
import {
  PostFilterComparison,
  PostFilterComparisonHumanReadable,
  PostFilterComparisonHumanReadableChronologic,
} from 'labstep-web/services/postFilter/constants';
import {
  getPostFilterMetadatasDate,
  getPostFilterMetadatasDatetime,
  getPostFilterMetadatasDefault,
  getPostFilterMetadatasNumeric,
  getPostFilterMetadatasOptions,
} from 'labstep-web/services/postFilter/filters/metadatas';
import type { PostFilterMetadatas } from 'labstep-web/services/postFilter/metadatas';
import type React from 'react';
import { useCallback, useMemo, useState } from 'react';
import { fieldsGeneric, fieldsPerMetadataType } from './fields';
import type {
  FilterMetadataType,
  IFilterMetadataFormProps,
} from './types';

const getPostFilterByPath = (path: string) => ({
  [MetadataType.default]: getPostFilterMetadatasDefault(path),
  [MetadataType.numeric]: getPostFilterMetadatasNumeric(path),
  [MetadataType.date]: getPostFilterMetadatasDate(path),
  [MetadataType.datetime]: getPostFilterMetadatasDatetime(path),
  [MetadataType.options]: getPostFilterMetadatasOptions(path),
});

const getPostFilterDefaultValue = (typeValue: MetadataType) => {
  const defaultValues: Record<
    MetadataType,
    { value: any; label: any }
  > = {
    [MetadataType.default]: {
      value: PostFilterComparison.eq,
      label: PostFilterComparisonHumanReadable.eq,
    },
    [MetadataType.numeric]: {
      value: PostFilterComparison.eq,
      label: PostFilterComparisonHumanReadable.eq,
    },
    [MetadataType.date]: {
      value: PostFilterComparison.lt,
      label: PostFilterComparisonHumanReadableChronologic.lt,
    },
    [MetadataType.datetime]: {
      value: PostFilterComparison.lt,
      label: PostFilterComparisonHumanReadableChronologic.lt,
    },
    [MetadataType.options]: {
      value: PostFilterComparison.contains,
      label: PostFilterComparisonHumanReadable.contains,
    },
    [MetadataType.file]: {
      value: PostFilterComparison.not_null,
      label: PostFilterComparisonHumanReadable.not_null,
    },
    [MetadataType.sequence]: {
      value: PostFilterComparison.eq,
      label: PostFilterComparisonHumanReadable.eq,
    },
    [MetadataType.entity_relation]: {
      value: PostFilterComparison.eq,
      label: PostFilterComparisonHumanReadable.eq,
    },
    [MetadataType.molecule]: {
      value: PostFilterComparison.eq,
      label: PostFilterComparisonHumanReadable.eq,
    },
  };

  return defaultValues[typeValue];
};

const FILTERS: Record<string, Record<string, PostFilterMetadatas>> = {
  metadatas: getPostFilterByPath('metadatas'),
  'protocolValues.resourceItem.resource.metadatas':
    getPostFilterByPath(
      'protocolValues.resourceItem.resource.metadatas',
    ),
  'protocolValues.resourceItem.metadatas': getPostFilterByPath(
    'protocolValues.resourceItem.metadatas',
  ),
  'purchase_order.metadatas': getPostFilterByPath(
    'purchaseOrder.metadatas',
  ),
  'resource.metadatas': getPostFilterByPath('resource.metadatas'),
};

export const FilterMetadataForm: React.FC<
  IFilterMetadataFormProps
> = ({
  addPostFilter,
  onDone,
  defaultValues: defaultValuesProps,
  path,
  replace,
}) => {
  const [metadataType, setMetadataType] =
    useState<FilterMetadataType>(MetadataType.default);

  const defaultValues = useMemo(
    () => ({
      type: {
        value: MetadataType.default,
        label: MetadataTypeValues[MetadataType.default],
      },
      ...defaultValuesProps,
      comparison: getPostFilterDefaultValue(
        defaultValuesProps
          ? defaultValuesProps.type.value
          : MetadataType.default,
      ),
    }),
    [],
  );

  const fields = useMemo(
    () => [
      ...fieldsGeneric,
      ...fieldsPerMetadataType(!!defaultValuesProps)[metadataType]
        .fields,
    ],
    [metadataType],
  );

  const onSubmit = useCallback(
    (values): void => {
      const { type, label, value, comparison } = values;

      const typeValue = type.value as
        | MetadataType.default
        | MetadataType.numeric
        | MetadataType.date
        | MetadataType.datetime
        | MetadataType.options;

      if (typeValue === MetadataType.numeric) {
        values.value = Number(value);
      }
      const filter = FILTERS[path][typeValue];
      addPostFilter(
        filter.getNode([
          label,
          [value, comparison?.value || PostFilterComparison.eq],
        ]),
        true,
      );
      onDone();
    },
    [path, addPostFilter],
  );

  const onFormChange = useCallback((values): void => {
    setMetadataType(values.type.value);
  }, []);

  return (
    <FormReusableGroup
      fields={fields}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      onFormChange={onFormChange}
      onDone={onDone}
      addAnother={false}
    />
  );
};
